import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import DualScore from "@/components/DualScore";
import EvidenceBadge from "@/components/EvidenceBadge";
import PropertyAutocomplete from "@/components/PropertyAutocomplete";
import { ArrowLeftRight, Search, Zap, Timer, Sparkles } from "lucide-react";
import { toast } from "sonner";

const INTAKE_TONE = {
  immediate: "bg-emerald-100 text-emerald-800",
  limited: "bg-amber-100 text-amber-800",
  long_waitlist: "bg-rose-100 text-rose-800",
  unknown: "bg-slate-100 text-slate-500",
};
const INTAKE_LABEL = { immediate: "Immediate", limited: "Limited", long_waitlist: "Long waitlist", unknown: "Unknown" };

export default function MatchingEngine() {
  const [selectedProp, setSelectedProp] = useState(null);
  const [propMatches, setPropMatches] = useState([]);
  const [sort, setSort] = useState("opportunity");
  const [intakeFilter, setIntakeFilter] = useState("all");

  const [programs, setPrograms] = useState([]);
  const [progSearch, setProgSearch] = useState("");
  const [selectedProg, setSelectedProg] = useState(null);
  const [progMatches, setProgMatches] = useState([]);
  const [rationales, setRationales] = useState({}); // {program_id: {rationale, loading}}

  useEffect(() => { api.get("/programs").then(r => { setPrograms(r.data); if (r.data[0]) setSelectedProg(r.data[0]); }); }, []);

  useEffect(() => {
    if (!selectedProp) return;
    const params = new URLSearchParams();
    if (sort === "time_to_occupancy") params.set("sort", "time_to_occupancy");
    if (intakeFilter !== "all") params.set("intake", intakeFilter);
    api.get(`/match/property/${selectedProp.id}?${params.toString()}`).then(r => setPropMatches(r.data.matches || []));
  }, [selectedProp, sort, intakeFilter]);

  useEffect(() => {
    if (selectedProg) api.get(`/match/program/${selectedProg.id}`).then(r => setProgMatches(r.data.matches || []));
  }, [selectedProg]);

  const fetchRationale = async (programId) => {
    if (!selectedProp) return;
    setRationales(r => ({ ...r, [programId]: { loading: true } }));
    try {
      const { data } = await api.post(`/match/property/${selectedProp.id}/ai-rationale/${programId}`);
      setRationales(r => ({ ...r, [programId]: { rationale: data.rationale, note: data.note, loading: false } }));
    } catch (err) {
      setRationales(r => ({ ...r, [programId]: { loading: false } }));
      toast.error(err?.response?.data?.detail || "AI rationale failed");
    }
  };

  const filteredPrograms = programs.filter(p =>
    progSearch === "" || `${p.name} ${p.agency} ${p.funder} ${p.vertical}`.toLowerCase().includes(progSearch.toLowerCase())
  ).slice(0, 8);

  return (
    <div className="space-y-6" data-testid="matching-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
          <ArrowLeftRight className="w-3.5 h-3.5" /> Bidirectional Engine
        </div>
        <h1 className="text-3xl font-bold">Matching Engine</h1>
        <p className="text-sm text-slate-500 mt-1">Search a property or a program to run a match in either direction. Time-to-occupancy sits alongside score.</p>
      </div>

      <Tabs defaultValue="prop2prog">
        <TabsList>
          <TabsTrigger value="prop2prog" data-testid="dir-prop-prog">Property → Programs</TabsTrigger>
          <TabsTrigger value="prog2prop" data-testid="dir-prog-prop">Program Need → Properties</TabsTrigger>
        </TabsList>

        <TabsContent value="prop2prog" className="mt-4 space-y-4">
          <Card className="p-4 border-slate-200">
            <PropertyAutocomplete testid="matching-property-search" placeholder="Type a property address to match…" onSelect={(p) => setSelectedProp(p)} />
            {selectedProp && (
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-600">
                  Matching: <span className="font-semibold">{selectedProp.address}</span> · {selectedProp.city}, TX · {selectedProp.bedrooms}BR/{selectedProp.bathrooms}BA
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sort</label>
                  <select value={sort} onChange={e => setSort(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" data-testid="matching-sort-select">
                    <option value="opportunity">Opportunity Score</option>
                    <option value="time_to_occupancy">Time-to-Occupancy</option>
                  </select>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold ml-2">Intake</label>
                  <select value={intakeFilter} onChange={e => setIntakeFilter(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" data-testid="matching-intake-filter">
                    <option value="all">All</option>
                    <option value="immediate">Immediate</option>
                    <option value="limited">Limited</option>
                    <option value="long_waitlist">Long waitlist</option>
                  </select>
                </div>
              </div>
            )}
          </Card>
          {propMatches.map(m => {
            const rat = rationales[m.program.id] || {};
            const intake = m.intake_capacity || "unknown";
            return (
              <Card key={m.program.id} className="p-4 border-slate-200">
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/programs/${m.program.id}`} className="font-semibold hover:text-[#C25E00]">{m.program.name}</Link>
                      <EvidenceBadge level={m.program.evidence_level} />
                      {m.program.rent_basis && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold uppercase tracking-wider">{m.program.rent_basis.replace("_"," ")}</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider inline-flex items-center gap-1 ${INTAKE_TONE[intake]}`} data-testid={`intake-${m.program.id}`}>
                        {intake === "immediate" ? <Zap className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
                        {INTAKE_LABEL[intake]}
                        {m.time_to_occupancy_days > 0 && <span className="opacity-75">· ~{m.time_to_occupancy_days}d</span>}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 capitalize mt-1">{m.program.vertical?.replace("_"," ")} · {m.program.agency}</div>
                    <ul className="text-xs text-emerald-700 mt-2 list-disc list-inside">{(m.reasons||[]).map((r,i)=><li key={i}>{r}</li>)}</ul>
                    {(m.penalties||[]).length > 0 && <ul className="text-xs text-rose-600 mt-1 list-disc list-inside">{m.penalties.map((r,i)=><li key={i}>{r}</li>)}</ul>}
                    <div className="mt-3">
                      {rat.rationale ? (
                        <div className="p-2.5 rounded-md bg-slate-50 border border-slate-200 text-xs" data-testid={`ai-rationale-${m.program.id}`}>
                          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1"><Sparkles className="w-3 h-3" /> AI Match Rationale — Draft</div>
                          <div className="text-slate-800">{rat.rationale}</div>
                          <div className="text-[10px] text-slate-500 mt-1 italic">{rat.note}</div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => fetchRationale(m.program.id)} disabled={rat.loading} data-testid={`ai-rationale-btn-${m.program.id}`}>
                          <Sparkles className="w-3.5 h-3.5 mr-1" /> {rat.loading ? "Drafting…" : "AI Match Rationale"}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="w-64 shrink-0"><DualScore opportunity={m.opportunity_score} confidence={m.confidence_score} /></div>
                </div>
              </Card>
            );
          })}
          {selectedProp && propMatches.length === 0 && <div className="text-sm text-slate-500">No matching programs match those filters — widen the intake filter.</div>}
        </TabsContent>

        <TabsContent value="prog2prop" className="mt-4 space-y-4">
          <Card className="p-4 border-slate-200 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={progSearch} onChange={e=>setProgSearch(e.target.value)} placeholder="Search programs by name, agency, funder, vertical..." className="pl-9" data-testid="matching-program-search"/>
            </div>
            <div className="flex flex-wrap gap-2">
              {filteredPrograms.map(p => (
                <button key={p.id} type="button" onClick={()=>setSelectedProg(p)}
                        data-testid={`matching-program-pill-${p.id}`}
                        className={`px-3 py-1 rounded-full text-xs border ${selectedProg?.id === p.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}>
                  {p.name}
                </button>
              ))}
            </div>
            {selectedProg && <div className="text-xs text-slate-600">Matching: <span className="font-semibold">{selectedProg.name}</span> · {selectedProg.agency}</div>}
          </Card>
          {progMatches.map(m => (
            <Card key={m.property.id} className="p-4 border-slate-200">
              <div className="flex justify-between items-start gap-6">
                <div className="flex-1 min-w-0">
                  <Link to={`/properties/${m.property.id}`} className="font-semibold hover:text-[#C25E00]">{m.property.address}</Link>
                  <div className="text-xs text-slate-500">{m.property.city}, {m.property.county} · {m.property.bedrooms}BR/{m.property.bathrooms}BA</div>
                  <ul className="text-xs text-emerald-700 mt-2 list-disc list-inside">{(m.reasons||[]).map((r,i)=><li key={i}>{r}</li>)}</ul>
                </div>
                <div className="w-64 shrink-0"><DualScore opportunity={m.opportunity_score} confidence={m.confidence_score} /></div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
